import { ElementType, useMemo, useState, useEffect } from "react"

import { Button, DateRangePicker } from "~/ui-rtk/components/ui/controls"
import {
  AggregationType,
  VISUALIZATION_TYPE,
  VISUALIZATIONS_MAP,
} from "~/ui-rtk/constants/charts"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"

import "./mind-share.scss"
import RichTooltip from "../../../ui/common/RichTooltip"
import Card from "../../../ui/common/Card"
import { InfoSvg } from "~/ui-rtk/components/ui/svg/essentials"
import Chart from "~/ui-rtk/components/ui/charts/Chart"
import {
  TMindShareChart,
  URL_WIDGET_PARAM_KEY,
} from "~/ui-rtk/pages/BrandHealth/MindShare/connect"
import { ComposedChart } from "../../../ui/charts"
import { MultiChartItem } from "../../../ui/charts/MultiChartItem/MultiChartItem"
import { startCase } from "lodash"
import Total from "./components/Total"
import { isValidDate } from "~/ui-rtk/hooks/period"
import useDate from "~/ui-rtk/hooks/date"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import { URL_SETTINGS_PARAM_KEY } from "~/ui-rtk/constants/url-params"
import { LastUpdatedChip } from "~/ui-rtk/components/ui/common/LastUpdatedChip/LastUpdatedChip"
import { useLastUpdateDate } from "~/ui-rtk/hooks/cube"
import {
  Cog8ToothIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline"
import TabsGroup from "~/ui-rtk/components/ui/controls/TabsGroup/TabsGroup"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { GranularityMapping } from "~/ui-rtk/constants/metrics-mapping"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import MindShareSettingsDialog from "./components/MindShareSettingsDialog/MindShareSettingsDialog"

type TDashboardCharts = Record<string, TMindShareChart>

export interface TBrandsHealthMindShareDashboardProps {
  title: string
  slug: string
  charts: TDashboardCharts
  parent: {
    slug: string
    title: string
  }
}

type TBrandHealthMindShareWidget = TMindShareChart & {
  component: ElementType
  groupValue?: string
  label?: string
  slug: string
}

const dayjs = useDate()

export default function BrandsHealthMindShareDashboard({
  title,
  charts,
  slug,
  parent,
}: TBrandsHealthMindShareDashboardProps) {
  const { params, setParams } = useURLSearchParams()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [index, setIndex] = useState(0)

  const currentCompany = useCurrentCompany()
  const { keywords } = (currentCompany?.metadata ?? {}) as {
    keywords: Record<string, unknown>
  }
  const categories = [
    ...(((keywords?.google as Record<string, unknown>)
      ?.categories as string[]) ?? []),
  ]

  const visibleTabs = useMemo(
    () =>
      Object.keys(charts).reduce(
        (
          plainCharts: Record<string, TBrandHealthMindShareWidget>,
          key: string,
        ) => {
          const chart = charts[key]
          if (chart.multi) {
            categories.forEach(category => {
              plainCharts[`${key}_${category}`.toUpperCase()] = {
                ...chart,
                slug: `${key}_${category}`,
                component: MultiChartItem,
                groupValue: category,
                label: chart.widgetLabelPrefix
                  ? `${chart.widgetLabelPrefix} ${startCase(category)}`
                  : startCase(category),
              }
            })
            return plainCharts
          }
          plainCharts[key] = {
            ...chart,
            slug: chart.id,
            component: ComposedChart,
          }
          return plainCharts
        },
        {} as Record<string, TBrandHealthMindShareWidget>,
      ),
    [charts],
  )

  const visibleTabsOptions = useMemo(
    () => Object.values(visibleTabs),
    [visibleTabs],
  )

  const defaultVisibleTab = useMemo(
    () => visibleTabsOptions[0],
    [visibleTabsOptions],
  )

  const widgetURLSearchParam =
    decodeURI(params[URL_WIDGET_PARAM_KEY]) ?? defaultVisibleTab.id

  const activeWidget = useMemo(() => {
    const widgetId = widgetURLSearchParam?.toUpperCase()

    if (widgetId && visibleTabs[widgetId]) {
      return visibleTabs[widgetId]
    }
    return defaultVisibleTab
  }, [widgetURLSearchParam, visibleTabs])

  const { dateMetric, filterMetrics } = activeWidget.lastDateChipProps

  const { lastDate, isLoading: isLastUpdateDateLoading } = useLastUpdateDate({
    dateMetric,
    filterMetrics,
  })

  // Calculate default date range based on lastDate or current date
  const defaultDateRange = useMemo(() => {
    const endDate = lastDate
      ? dayjs(lastDate).toDate()
      : dayjs().endOf("month").toDate()
    const startDate = dayjs(endDate).subtract(12, "months").toDate()
    return [startDate, endDate] as [Date, Date]
  }, [lastDate])

  // Local state for date range - not from URL params
  const [localDateRange, setLocalDateRange] =
    useState<[Date, Date]>(defaultDateRange)

  useEffect(() => {
    setLocalDateRange(defaultDateRange)
  }, [defaultDateRange])

  // Custom predefined periods for MindShare
  const mindSharePredefinedPeriods = [
    { label: "Last 3 months", days: 90 },
    { label: "Last 6 months", days: 180 },
    { label: "Last 12 months", days: 365 },
    { label: "Last 24 months", days: 730 },
  ]

  const chartProps = useMemo(
    () => VISUALIZATIONS_MAP[activeWidget.widget]?.props,
    [activeWidget],
  )

  const cubeQueryParams = useMemo(
    () => ({
      [activeWidget.widget]: {
        granularity: GranularityMapping.MONTHLY,
      },
    }),
    [activeWidget],
  )

  const handleChangePeriods = ({ start, end }: { start: Date; end: Date }) => {
    setLocalDateRange([start, end])
  }

  const handleSetDataAggType = () => {
    // Granularity is fixed to MONTHLY for this page
  }

  const handleSetWidget = (idx: number) => {
    setParams({
      [URL_WIDGET_PARAM_KEY]: encodeURI(
        visibleTabsOptions[idx].slug.toLocaleLowerCase(),
      ),
    })
  }

  const handleOpenSettings = () => {
    setSettingsOpen(true)
  }

  // The sidebar's "Keywords & Competitors" entry (free-SoS minimal nav,
  // APP-493) deep-links here with ?settings=… to open the settings dialog.
  // Consume the param so closing the dialog doesn't leave a URL behind that
  // would reopen it, and so re-clicking the nav entry opens it again. The
  // clear must REPLACE the history entry — a push would leave the
  // ?settings URL behind it, so Back would land on it, refire this effect,
  // and trap the user in a reopening loop.
  const settingsUrlParam = params[URL_SETTINGS_PARAM_KEY]
  useEffect(() => {
    if (settingsUrlParam) {
      setSettingsOpen(true)
      setParams({ [URL_SETTINGS_PARAM_KEY]: undefined }, { replace: true })
    }
  }, [settingsUrlParam])

  const { isEnabled: isDemoEnabled } = useDemoMode()

  const findActiveIndex = () =>
    visibleTabsOptions.findIndex(({ slug }) => activeWidget?.slug === slug)

  useEffect(() => {
    setIndex(findActiveIndex())
  }, [activeWidget])
  return (
    <div className="px-system-md">
      <div className="flex justify-start items-center mb-system-lg">
        <Breadcrumbs
          items={[
            {
              slug: parent.slug,
              name: parent.title,
            },
            {
              slug,
              name: title,
            },
          ]}
        />

        <RichTooltip
          content={
            <Card className="relative flex items-center justify-center cursor-pointer bg-transparent border-none p-0">
              <span className="text-md">
                Google Keywords data updates on our platform occur monthly.
                <br /> It may take 4-6 weeks for new data to be reflected, based
                on Google's release schedule.
              </span>
            </Card>
          }
        >
          <InformationCircleIcon className="w-system-md h-system-md ml-system-sm cursor-pointer" />
        </RichTooltip>
      </div>

      <div className="ui-rtk-overview-dashboard border-b border-basic-blue">
        <TabsGroup
          options={visibleTabsOptions}
          onChange={handleSetWidget}
          index={index}
        >
          {(tab, idx) => (
            <div
              className={cn("flex items-center gap-2", {
                "blur-md":
                  isDemoEnabled &&
                  !["TOTAL", "BRAND_SHARE"].includes(
                    visibleTabsOptions[idx].slug,
                  ),
              })}
            >
              <span>{tab.label}</span>
              {idx === index &&
                VISUALIZATIONS_MAP[activeWidget.widget].description && (
                  <RichTooltip
                    content={
                      <Card
                        rounded
                        className="min-w-128 max-w-180 whitespace-normal text-start leading-5 bg-transparent p-0"
                      >
                        {VISUALIZATIONS_MAP[activeWidget.widget].description}
                      </Card>
                    }
                  >
                    <InfoSvg size={12} fill="white" />
                  </RichTooltip>
                )}
            </div>
          )}
        </TabsGroup>
      </div>
      <div className="flex justify-end items-stretch gap-system-sm mt-system-lg">
        <Button
          size="sm"
          className="flex justify-start text-system-gray-300  items-center gap-system-xs bg-system-blue-700 rounded-system-md border border-system-blue-600 "
          onClick={handleOpenSettings}
        >
          <Cog8ToothIcon className="w-system-md h-system-md" />
          <p className="text-md font-normal"> Settings</p>
        </Button>

        {localDateRange?.[0] &&
          localDateRange?.[1] &&
          isValidDate(localDateRange[0]) &&
          isValidDate(localDateRange[1]) &&
          !isLastUpdateDateLoading &&
          lastDate && (
            <DateRangePicker
              start={localDateRange[0]}
              comparisonEnabled={false}
              end={localDateRange[1]}
              granularity={AggregationType.MONTHLY}
              onChange={handleChangePeriods}
              lastUpdatedDate={lastDate ? new Date(lastDate) : undefined}
              showPredefinedPeriods={true}
              customPredefinedPeriods={mindSharePredefinedPeriods}
            />
          )}
        <RichTooltip
          content={
            <LastUpdatedChip
              usedInTooltip={true}
              lastDate={lastDate}
              isLoading={isLastUpdateDateLoading}
            >
              Max date available in Page:
            </LastUpdatedChip>
          }
        >
          <Card className="p-system-xs relative h-system-2xl w-system-2xl flex items-center justify-center rounded-system-md bg-system-blue-700 border border-system-blue-600 cursor-pointer">
            <span className="absolute -top-system-3xs -right-system-3xs flex size-system-sm">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-system-brand-pink-400 opacity-75"></span>
              <span className="relative inline-flex size-system-sm rounded-full bg-system-brand-pink-500"></span>
            </span>
            <InformationCircleIcon className="w-system-lg h-system-lg" />
          </Card>
        </RichTooltip>
      </div>
      <div className="mt-3">
        <Chart
          chartId={activeWidget.widget}
          className="h-[calc(100vh-300px)]"
          widgetType={VISUALIZATION_TYPE.MultiChart}
          component={activeWidget.component}
          dateRange={localDateRange}
          groupValue={activeWidget.groupValue}
          granularity={AggregationType.MONTHLY}
          hideAggSelector={true}
          setDataAggType={handleSetDataAggType}
          totalComponent={Total}
          hideTooltipTitle={true}
          enableChartDataExport={true}
          chartDataExportFilename="brand-health-mind-share-chart-data"
          cubeQueryParams={cubeQueryParams}
          {...chartProps}
        />
      </div>

      {settingsOpen && (
        <MindShareSettingsDialog onClose={() => setSettingsOpen(false)} />
      )}
    </div>
  )
}
